import CloseRounded from '@mui/icons-material/CloseRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import type { SxProps } from '@mui/material/styles';
import { alpha, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { createContext, type ReactNode,use, useId, useMemo } from 'react';

/**
 * The redesign's dialog chrome as a compound component: an icon tile, a
 * title/subtitle column, a close button, dividers at the section boundaries and
 * a right-aligned actions row. Consumers compose exactly the parts they need:
 * a form dialog takes all of them, a read-only dialog stops after `Content`,
 * and any wrapper (e.g. `<Box component="form">`) can sit between the root and
 * the parts, since the shared wiring travels by context.
 *
 * The primary action stays consumer-supplied (its type/color/disabled/spinner
 * differ per dialog); `Cancel` is provided because it is identical everywhere
 * and needs `onClose` from the context.
 *
 * ```tsx
 * <AppDialog open={open} onClose={close}>
 *   <AppDialog.Header>
 *     <AppDialog.Icon><BadgeRounded /></AppDialog.Icon>
 *     <AppDialog.HeaderText>
 *       <AppDialog.Title>New thing</AppDialog.Title>
 *       <AppDialog.Subtitle>What this dialog is for.</AppDialog.Subtitle>
 *     </AppDialog.HeaderText>
 *     <AppDialog.CloseButton />
 *   </AppDialog.Header>
 *   <AppDialog.Content>…fields…</AppDialog.Content>
 *   <AppDialog.Actions>
 *     <AppDialog.Cancel />
 *     <Button type="submit" variant="soft" color="primary">Create</Button>
 *   </AppDialog.Actions>
 * </AppDialog>
 * ```
 */

interface AppDialogContextValue {
  /** Generated in the root; `Title` takes it so `aria-labelledby` always resolves. */
  titleId: string;
  onClose: () => void;
}

const AppDialogContext = createContext<AppDialogContextValue | null>(null);

function useAppDialogContext(): AppDialogContextValue {
  const ctx = use(AppDialogContext);
  if (!ctx) throw new Error('AppDialog.* must be rendered inside <AppDialog>');
  return ctx;
}

const paperSlotProps = { paper: { sx: { borderRadius: '16px' } } };

const headerSx: SxProps<Theme> = { display: 'flex', alignItems: 'flex-start', gap: 1.75, p: 3, pb: 2 };
const headerTextSx: SxProps<Theme> = { flex: 1, minWidth: 0 };
const titleSx: SxProps<Theme> = { fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.3 };
const subtitleSx: SxProps<Theme> = { mt: 0.25, color: 'text.secondary' };
const closeButtonSx: SxProps<Theme> = { mt: -0.5, mr: -0.5, color: 'text.secondary' };
const contentSx: SxProps<Theme> = { pt: 2.5 };
const actionsSx: SxProps<Theme> = { px: 3, py: 2 };

/**
 * The tile: a hue at 14% with the same hue as ink. `ink` is separate from `tint`
 * because for gold they are not the same colour: the tint keeps the brand hue,
 * the ink has to darken for light or it disappears into it (see `SOFT_INK` in
 * `palette.ts`, which measures gold at 1.54:1 on its own tint).
 */
const iconTileSx = (ink: string, tint: string = ink) => ({
  flexShrink: 0,
  width: 44,
  height: 44,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: alpha(tint, 0.14),
  color: ink,
});

const toneSx = {
  // Per-mode teal (the focus-ring token): the brand teal in dark, a darker
  // legible teal in light; a bright-teal icon washes out on the pale tint.
  accent: (t: Theme) => iconTileSx(t.palette.focusRing),
  /**
   * Consequential but reversible: archiving, suspending. Distinct from `danger`
   * on purpose: red on an action that deletes nothing spends the signal a real
   * deletion will need, and it made the archive dialog contradict itself, a red
   * tile over an amber button.
   */
  warning: (t: Theme) => iconTileSx(t.palette.softInk.warning, t.palette.warning.main),
  danger: (t: Theme) => iconTileSx(t.palette.error.main),
} as const;

export interface AppDialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: DialogProps['maxWidth'];
  /**
   * Once the closing fade is over and the page is no longer `aria-hidden`: the
   * place to give the focus back to something of the page by hand.
   */
  onExited?: () => void;
}

function Root({ open, onClose, children, maxWidth = 'xs', onExited }: AppDialogProps) {
  const titleId = useId();
  const value = useMemo(() => ({ titleId, onClose }), [titleId, onClose]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby={titleId}
      slotProps={onExited ? { ...paperSlotProps, transition: { onExited } } : paperSlotProps}
    >
      <AppDialogContext value={value}>{children}</AppDialogContext>
    </Dialog>
  );
}

function Header({ children }: { children: ReactNode }) {
  return <Box sx={headerSx}>{children}</Box>;
}

export interface AppDialogIconProps {
  /**
   * Tile tint: the brand accent by default, amber for a consequential but
   * reversible action, the error red for a destructive one.
   */
  tone?: 'accent' | 'warning' | 'danger';
  children: ReactNode;
}

function Icon({ tone = 'accent', children }: AppDialogIconProps) {
  return <Box sx={toneSx[tone]}>{children}</Box>;
}

function HeaderText({ children }: { children: ReactNode }) {
  return <Box sx={headerTextSx}>{children}</Box>;
}

function Title({ children }: { children: ReactNode }) {
  const { titleId } = useAppDialogContext();
  return (
    <Typography id={titleId} variant="h6" sx={titleSx}>
      {children}
    </Typography>
  );
}

function Subtitle({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" sx={subtitleSx}>
      {children}
    </Typography>
  );
}

export interface AppDialogDismissProps {
  /**
   * Shown inactive while the dialog's action is in flight, so it reads as
   * unavailable instead of silently doing nothing. The consumer still blocks
   * Escape and the backdrop through the root's `onClose`.
   */
  disabled?: boolean;
}

function CloseButton({ disabled = false }: AppDialogDismissProps) {
  const { onClose } = useAppDialogContext();
  return (
    <IconButton onClick={onClose} disabled={disabled} size="small" aria-label="Close" sx={closeButtonSx}>
      <CloseRounded fontSize="small" />
    </IconButton>
  );
}

function Content({ children }: { children: ReactNode }) {
  return (
    <>
      <Divider />
      <DialogContent sx={contentSx}>{children}</DialogContent>
    </>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return (
    <>
      <Divider />
      <DialogActions sx={actionsSx}>{children}</DialogActions>
    </>
  );
}

function Cancel({ children, disabled = false }: AppDialogDismissProps & { children?: ReactNode }) {
  const { onClose } = useAppDialogContext();
  return (
    <Button onClick={onClose} disabled={disabled} color="inherit">
      {children ?? 'Cancel'}
    </Button>
  );
}

export const AppDialog = Object.assign(Root, {
  Header,
  Icon,
  HeaderText,
  Title,
  Subtitle,
  CloseButton,
  Content,
  Actions,
  Cancel,
});
